import React, {
  useState, useRef, useEffect, useMemo,
} from 'react';
import ReactSlider from 'react-slider';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';
import {
  AddPlus,
  MinusCircle,
} from '@picsio/icons';
import { IconButton } from '@picsio/ui';
import s from './RangeSlider.module.scss';
import Tooltip from '../../Tooltip';
import Logger from '../../../services/Logger';

const sliderLabels = [
  { name: 'Broad', value: 20 },
  { name: 'Medium', value: 40 },
  { name: 'Balanced', value: 60 },
  { name: 'Precise', value: 80 },
  { name: 'Exact', value: 100 },
];

const RangeSlider = ({
  initValue = 55,
  onChange,
  onSliderClick,
  onAfterChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(initValue);
  const min = 20;
  const max = 100;
  const sliderRef = useRef(null);

  const closestLabel = useMemo(() => sliderLabels.reduce((prev, curr) => (Math.abs(curr.value - value) < Math.abs(prev.value - value) ? curr : prev)), [value]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (sliderRef.current && !sliderRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLabelClick = (e) => {
    const labelValue = Number(e.currentTarget.dataset.value);
    const closest = sliderLabels.reduce((prev, curr) => (Math.abs(curr.value - labelValue) < Math.abs(prev.value - labelValue) ? curr : prev));
    Logger.log('User', 'SimilarityScorePanelPresetClicked', {
      similarity_level: closest.value,
      similarity_preset: closest.name,
    });
    setValue(labelValue);

    if (onAfterChange) onAfterChange(labelValue);
    if (onChange) onChange(labelValue);
  };

  const handleChange = (v) => {
    setValue(v);

    if (onChange) onChange(v);
  };

  const handleAfterChange = (v) => {
    const closest = sliderLabels.reduce((prev, curr) => (Math.abs(curr.value - v) < Math.abs(prev.value - v) ? curr : prev));

    Logger.log('User', 'SimilarityScorePanelPresetClicked', {
      similarity_level: v,
      similarity_preset: closest.name,
    });

    if (onAfterChange) onAfterChange(v);
  };

  const handleChangeInput = (e) => {
    let newValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
    newValue = newValue.replace(/^0+(?=\d)/, ''); // Remove leading zeros
    newValue = Number(newValue);
    Logger.log('User', 'SimilarityScorePanelNumericLevelChanged', {
      similarity_level: newValue,
      action: 'input',
    });
    setValue(newValue);
  };

  const handleBlurInput = (e) => {
    let newValue = e.target.value;
    if (newValue === '') newValue = min;
    newValue = Number(newValue);
    if (newValue < min) newValue = min;
    if (newValue > max) newValue = max;
    setValue(newValue);
    if (onChange) onChange(newValue);
    if (onAfterChange) onAfterChange(newValue);
  };

  const handleKeyDownInput = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleValueIncrement = () => {
    let newValue = value + 1;
    Logger.log('User', 'SimilarityScorePanelBtnLevelChanged', {
      similarity_level: newValue,
      action: 'increment',
    });
    if (newValue > max) newValue = max;
    setValue(newValue);
    if (onChange) onChange(newValue);
    if (onAfterChange) onAfterChange(newValue);
  };

  const handleValueDecrement = () => {
    let newValue = value - 1;
    Logger.log('User', 'SimilarityScorePanelBtnLevelChanged', {
      similarity_level: newValue,
      action: 'decrement',
    });
    if (newValue < min) newValue = min;
    setValue(newValue);
    if (onChange) onChange(newValue);
    if (onAfterChange) onAfterChange(newValue);
  };

  const toggleSliderOpen = () => {
    if (!isOpen) {
      Logger.log('User', 'SimilarityScoreDropdownClicked', {
        initial_preset: closestLabel.name,
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className={s.rangeSlider} ref={sliderRef}>
      <Tooltip content={<div style={{ textAlign: 'center' }}>Drag the Similarity slider to control how closely <br /> results should match your selected image.<br />Lower values explore styles;<br />higher values return almost identical images.</div>} placement="right">
        <div onClick={toggleSliderOpen} className={cn(s.trigger, { [s.isOpen]: isOpen })} role="presentation" data-testid="similarityScoreTrigger">
          <span className={s.triggerText}>
            {closestLabel.name} ({value.toFixed(0)}%)
          </span>
          <i className={s.arrow} />
        </div>
      </Tooltip>

      <CSSTransition
        unmountOnExit
        in={isOpen}
        timeout={300}
        classNames="fade"
      >
        <div className={s.container}>
          <div className={s.header}>
            <div className={s.title}>Similarity level</div>
            <div className={s.valueContainer}>
              <IconButton
                onClick={handleValueDecrement}
                size="md"
                disabled={value <= min}
                componentProps={{
                  'data-testid': 'similarityScoreDecrementBtn',
                }}
              >
                <MinusCircle />
              </IconButton>
              <input
                id="range-slider-input"
                name="range-slider-input"
                type="text"
                value={value.toFixed(0)}
                onBlur={handleBlurInput}
                onKeyDown={handleKeyDownInput}
                onChange={handleChangeInput}
                className={s.value}
                data-testid="similarityScoreInput"
              />
              <IconButton
                onClick={handleValueIncrement}
                size="md"
                disabled={value >= max}
                componentProps={{
                  'data-testid': 'similarityScoreIncrementBtn',
                }}
              >
                <AddPlus />
              </IconButton>
            </div>
          </div>
          <div className={s.sliderWrapper}>
            <ReactSlider
              min={min}
              max={max}
              value={value.toFixed(0)}
              className={s.slider}
              handleClassName={s.dot}
              barClassName={s.line}
              onChange={handleChange}
              onAfterChange={handleAfterChange}
              onSliderClick={onSliderClick}
            />
            <div>
              {sliderLabels.map((label) => {
                const isPassed = Number(value) >= label.value;
                const isActive = Number(value) === label.value;

                return (
                  <div
                    key={label.name}
                    className={cn(s.sliderLabel, {
                      [s.passed]: isPassed,
                      [s.active]: isActive,
                    })}
                    data-value={label.value}
                    onClick={handleLabelClick}
                    role="presentation"
                    data-testid={`similarityScoreLabel-${label.name}`}
                  >
                    <div className={s.sliderLabelText}>{label.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CSSTransition>

    </div>
  );
};

export default RangeSlider;
